import type { UserSpreadsheet } from '@/services/userData'

/**
 * Last fetched spreadsheet list, kept outside React so it survives remounts.
 *
 * The dashboard switches views by remounting (SideBarView's activeItem switch), so
 * without this every return to Home reset the list to empty and refetched, flashing
 * the sheets away. It also lets Home and My Sheets share one fetch.
 *
 * In its own module rather than inside the hook so the uid rule below can be tested
 * without rendering anything.
 */
let entry: { uid: string; spreadsheets: UserSpreadsheet[]; storedAt: number } | null = null

/**
 * How long a cached list is served without checking the server.
 *
 * A hit is still returned immediately past this — the point of the cache is that the
 * list never flashes away — but the hook refetches in the background and updates when
 * the answer lands. Without a bound, a sheet created in another tab would never
 * appear here: that tab's clear cannot reach this module instance, and SPA navigation
 * does not reload it, so the stale list would survive until a hard refresh.
 */
export const CACHE_TTL_MS = 30_000

/**
 * Bumped on every clear, so a read that was already in flight when the cache was
 * invalidated cannot repopulate it with what it fetched beforehand.
 *
 * Without this: a read starts, a spreadsheet is created and clears the cache, then
 * the read resolves and writes the pre-creation list back. The new sheet is then
 * missing from every later view until something else invalidates — which is exactly
 * the failure the invalidation exists to prevent.
 */
let generation = 0

/** Token to capture before a read and hand back to setCachedSpreadsheets. */
export const currentGeneration = (): number => generation

/**
 * The cached list, but only if it belongs to this user.
 *
 * The uid check is the point of this module, not a detail: the hook re-runs when the
 * user changes, and a list shared across accounts would hand one user's spreadsheet
 * titles and URLs to whoever signs in next on the same tab. Reachable since V1
 * gained real sign-in.
 */
export const getCachedSpreadsheets = (uid: string | undefined): UserSpreadsheet[] | null =>
  uid && entry?.uid === uid ? entry.spreadsheets : null

/**
 * Whether this user's cached list is old enough to be revalidated in the background.
 * True when there is nothing cached for them, so a caller can treat "no hit" and
 * "stale hit" the same way.
 */
export const isCacheStale = (uid: string | undefined, now: number = Date.now()): boolean =>
  !uid || entry?.uid !== uid || now - entry.storedAt >= CACHE_TTL_MS

/**
 * Store a freshly read list, unless the cache was cleared while the read was in
 * flight — pass the generation captured before starting it.
 */
export const setCachedSpreadsheets = (
  uid: string,
  spreadsheets: UserSpreadsheet[],
  generationAtRequest: number
) => {
  if (generationAtRequest !== generation) return
  entry = { uid, spreadsheets, storedAt: Date.now() }
}

/**
 * Drop the cached list.
 *
 * Called on sign-out, on an explicit refresh, and after a spreadsheet is created —
 * onboarding creates one and navigates straight to the dashboard, so a stale cache
 * would hide the sheet the user just made.
 */
export const clearCachedSpreadsheets = () => {
  entry = null
  generation++
}
